import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { SupportTicketDetails } from 'chaika-frontend-types/frontend-cms';

import { getSupportErrorCode, SupportApi } from '@api/SupportApi';

import { supportKeys, supportQueryOptions } from '../queries/supportQueries';
import { SupportTicketListFilter } from '../utils/ticketListFilter';

interface Params {
    filter: SupportTicketListFilter;
    onTicketCreated(ticketId: string): void;
}

const CREATE_TICKET_MUTATION_KEY = [...supportKeys.all, 'createTicket'];

export function useTicketList({ filter, onTicketCreated }: Params) {
    const queryClient = useQueryClient();
    const ticketsQuery = useInfiniteQuery(supportQueryOptions.ticketList(filter));
    const statusesQuery = useQuery(supportQueryOptions.ticketStatuses());
    const createTicketMutation = useMutation({
        mutationKey: CREATE_TICKET_MUTATION_KEY,
        retry: false,
        mutationFn: (title: string) => SupportApi.createTicket(title),
        onSuccess: ticket => {
            queryClient.setQueryData<SupportTicketDetails>(supportKeys.ticket(ticket.id), {
                ...ticket,
                responsible: null,
                participants: [],
            });
            queryClient.invalidateQueries({ queryKey: supportKeys.ticketLists() });
            onTicketCreated(ticket.id);
        },
    });

    return {
        tickets: ticketsQuery.data?.pages.flatMap(page => page.items) ?? [],
        isLoading: ticketsQuery.isLoading,
        error: ticketsQuery.error,
        hasNextPage: ticketsQuery.hasNextPage,
        isFetchingNextPage: ticketsQuery.isFetchingNextPage,
        statuses: statusesQuery.data,
        loadNextPage: async () => {
            const { error } = await ticketsQuery.fetchNextPage();
            if (getSupportErrorCode(error) === 'INVALID_CURSOR') {
                await queryClient.resetQueries({ queryKey: supportKeys.ticketList(filter), exact: true });
            }
        },
        createTicket: (title: string) => {
            if (!queryClient.isMutating({ mutationKey: CREATE_TICKET_MUTATION_KEY })) {
                createTicketMutation.mutate(title);
            }
        },
        isCreatingTicket: createTicketMutation.isPending,
        createTicketError: createTicketMutation.error,
    };
}
